/**
 * Developer Task Board Component
 * Drag-and-drop Kanban board for managing development tasks
 */

import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Avatar,
  IconButton,
  Stack,
  Paper,
  Tooltip,
  Menu,
  MenuItem,
  LinearProgress,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Schedule as ScheduleIcon,
  Flag as FlagIcon,
  Comment as CommentIcon,
  AttachFile as AttachFileIcon,
} from '@mui/icons-material';

export interface Task {
  id: number;
  title: string;
  description?: string;
  status: 'todo' | 'in_progress' | 'review' | 'done';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  dueDate?: string;
  estimatedHours?: number;
  loggedHours?: number;
  projectName?: string;
  assignee?: {
    name: string;
    avatar?: string;
  };
  comments?: number;
  attachments?: number;
}

interface TaskBoardProps {
  tasks: Task[];
  onTaskMove?: (taskId: number, newStatus: Task['status']) => void;
  onTaskClick?: (task: Task) => void;
}

const TaskBoard: React.FC<TaskBoardProps> = ({
  tasks,
  onTaskMove,
  onTaskClick,
}) => {
  const [draggedTask, setDraggedTask] = useState<number | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);

  const columns: { status: Task['status']; title: string; color: string }[] = [
    { status: 'todo', title: 'To Do', color: '#9e9e9e' },
    { status: 'in_progress', title: 'In Progress', color: '#2196f3' },
    { status: 'review', title: 'In Review', color: '#ff9800' },
    { status: 'done', title: 'Done', color: '#4caf50' },
  ];

  const getPriorityColor = (priority: Task['priority']) => {
    switch (priority) {
      case 'urgent':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      default:
        return 'default';
    }
  };

  const handleDragStart = (taskId: number) => {
    console.log('Drag started:', taskId);
    setDraggedTask(taskId);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (status: Task['status']) => {
    console.log('Drop event:', draggedTask, status, 'onTaskMove exists:', !!onTaskMove);
    if (draggedTask && onTaskMove) {
      onTaskMove(draggedTask, status);
    }
    setDraggedTask(null);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, task: Task) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedTask(task);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTask(null);
  };

  const handleTaskCardClick = (task: Task) => {
    console.log('Task card clicked:', task.id, 'onTaskClick exists:', !!onTaskClick);
    if (onTaskClick) {
      onTaskClick(task);
    }
  };

  const getTasksByStatus = (status: Task['status']) => {
    return tasks.filter((task) => task.status === status);
  };

  const formatDueDate = (dateString?: string) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.ceil((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return { text: 'Overdue', color: 'error' };
    } else if (diffDays === 0) {
      return { text: 'Due today', color: 'warning' };
    } else if (diffDays <= 3) {
      return { text: `${diffDays} days left`, color: 'warning' };
    } else {
      return { text: date.toLocaleDateString(), color: 'default' };
    }
  };

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        sx={{ overflowX: 'auto', pb: 2 }}
      >
        {columns.map((column) => {
          const columnTasks = getTasksByStatus(column.status);
          return (
            <Paper
              key={column.status}
              sx={{
                minWidth: { xs: '100%', md: '280px' },
                flex: 1,
                p: 2,
                bgcolor: 'background.default',
              }}
              onDragOver={handleDragOver}
              onDrop={() => handleDrop(column.status)}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      bgcolor: column.color,
                    }}
                  />
                  <Typography variant="h6" fontWeight="medium">
                    {column.title}
                  </Typography>
                </Box>
                <Chip label={columnTasks.length} size="small" />
              </Box>

              <Stack spacing={2}>
                {columnTasks.map((task) => {
                  const dueDate = formatDueDate(task.dueDate);
                  const progress = task.estimatedHours
                    ? Math.min(
                        ((task.loggedHours || 0) / task.estimatedHours) * 100,
                        100
                      )
                    : 0;

                  return (
                    <Card
                      key={task.id}
                      draggable
                      onDragStart={() => handleDragStart(task.id)}
                      onClick={() => handleTaskCardClick(task)}
                      sx={{
                        cursor: 'pointer',
                        opacity: draggedTask === task.id ? 0.5 : 1,
                        '&:hover': {
                          boxShadow: 3,
                          transform: 'translateY(-2px)',
                          transition: 'all 0.2s',
                        },
                      }}
                    >
                      <CardContent>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="start"
                          mb={1}
                        >
                          <Box flex={1}>
                            <Typography variant="subtitle2" gutterBottom>
                              {task.title}
                            </Typography>
                            {task.description && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  mb: 1,
                                }}
                              >
                                {task.description}
                              </Typography>
                            )}
                          </Box>
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, task)}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Box>

                        {task.projectName && (
                          <Chip
                            label={task.projectName}
                            size="small"
                            variant="outlined"
                            sx={{ mb: 1 }}
                          />
                        )}

                        <Stack direction="row" spacing={0.5} flexWrap="wrap" mb={1}>
                          <Chip
                            label={task.priority}
                            size="small"
                            color={getPriorityColor(task.priority) as any}
                            icon={<FlagIcon />}
                          />
                          {dueDate && (
                            <Chip
                              label={dueDate.text}
                              size="small"
                              color={dueDate.color as any}
                              icon={<ScheduleIcon />}
                            />
                          )}
                        </Stack>

                        {task.estimatedHours && (
                          <Box mb={1}>
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              mb={0.5}
                            >
                              <Typography variant="caption" color="text.secondary">
                                Progress
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {task.loggedHours || 0}h / {task.estimatedHours}h
                              </Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={progress}
                              sx={{ height: 6, borderRadius: 1 }}
                            />
                          </Box>
                        )}

                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Stack direction="row" spacing={1}>
                            {task.comments && task.comments > 0 && (
                              <Tooltip title={`${task.comments} comments`}>
                                <Box display="flex" alignItems="center" gap={0.5}>
                                  <CommentIcon
                                    fontSize="small"
                                    sx={{ color: 'text.secondary', fontSize: 16 }}
                                  />
                                  <Typography variant="caption" color="text.secondary">
                                    {task.comments}
                                  </Typography>
                                </Box>
                              </Tooltip>
                            )}
                            {task.attachments && task.attachments > 0 && (
                              <Tooltip title={`${task.attachments} attachments`}>
                                <Box display="flex" alignItems="center" gap={0.5}>
                                  <AttachFileIcon
                                    fontSize="small"
                                    sx={{ color: 'text.secondary', fontSize: 16 }}
                                  />
                                  <Typography variant="caption" color="text.secondary">
                                    {task.attachments}
                                  </Typography>
                                </Box>
                              </Tooltip>
                            )}
                          </Stack>

                          {task.assignee && (
                            <Tooltip title={task.assignee.name}>
                              <Avatar
                                sx={{ width: 24, height: 24, fontSize: '0.75rem' }}
                                src={task.assignee.avatar}
                              >
                                {task.assignee.name
                                  .split(' ')
                                  .map((n) => n[0])
                                  .join('')}
                              </Avatar>
                            </Tooltip>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}

                {columnTasks.length === 0 && (
                  <Box
                    sx={{
                      p: 3,
                      textAlign: 'center',
                      color: 'text.secondary',
                      borderRadius: 1,
                      border: '2px dashed',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="body2">No tasks</Typography>
                  </Box>
                )}
              </Stack>
            </Paper>
          );
        })}
      </Stack>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleMenuClose}>View Details</MenuItem>
        <MenuItem onClick={handleMenuClose}>Edit</MenuItem>
        <MenuItem onClick={handleMenuClose}>Add Comment</MenuItem>
        <MenuItem onClick={handleMenuClose}>Log Time</MenuItem>
      </Menu>
    </Box>
  );
};

export default TaskBoard;
